import Fuse from 'fuse.js';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { UserProfile } from '@gitbook/app-database';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { getSuspenseSafe, mapSuspenseArray } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { apiResource, type InAppSearchResult, readAPICache, searchCache } from '../../state';
import { flattenSpacesInOrgAllContent, getSpaceFromOrgAllContent } from '../Organization';

export type ContentSearchResult =
    | ContentSearchResultSpace
    | ContentSearchResultPage
    | ContentSearchResultSection;

export interface ContentSearchResultSpace {
    type: 'space';
    key: string;
    title: string;
    space: api.Space;
    groups: ContentSearchGroup[];
}

export interface ContentSearchResultPage {
    type: 'page';
    key: string;
    title: string;
    path: string;
    space: api.Space;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    groups: ContentSearchGroup[];
}

export interface ContentSearchResultSection {
    type: 'section';
    key: string;
    title: string;
    body: string;
    space: api.Space;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    path: string;
    anchor: string | undefined;
    groups: ContentSearchGroup[];
}

export type ContentSearchGroup =
    | ContentSearchGroupOwner
    | ContentSearchGroupSpace
    | ContentSearchGroupPage;

export type ContentSearchGroupOwner = ContentSearchGroupUser | ContentSearchGroupOrganization;

interface ContentSearchGroupUser {
    type: 'user';
    user: UserProfile;
}

interface ContentSearchGroupOrganization {
    type: 'organization';
    organization: api.Organization;
}

export interface ContentSearchGroupSpace {
    type: 'space';
    space: api.Space;
}

export interface ContentSearchGroupPage {
    type: 'page';
    space: api.Space;
    page: hiveCore.RevisionPage;
}

type SearchResultsContext = {
    organization: string | null;
    hiveClient: HiveClient;
};

// Defined here so we can remove it from the list of Algolia indexes
type InAppSearchEntryObject =
    | InAppSearchResult
    | {
          kind: 'space';
          space: api.Space;
      };

/**
 * Search spaces from organization content, filtering by title and prioritizing exact matches.
 */
export function searchSpaces(
    query: string,
    organization: string
): {
    kind: 'space';
    space: api.Space;
}[] {
    const orgAllContent = readAPICache(
        apiResource(
            'getOrganizationAllContent',
            { organizationId: organization },
            { nullOnError: true }
        )
    );

    if (!orgAllContent) {
        return [];
    }

    if (!query) {
        return orgAllContent.spaces
            .map((item) => ('space' in item ? item.space : null))
            .filter((space): space is api.Space => !!space)
            .map((space) => ({ kind: 'space', space }));
    }

    return getOrganizationSpacesFuse(orgAllContent)
        .search(query)
        .map(({ item }) => ({ kind: 'space', space: item }));
}

const organizationSpacesFuseCache = new WeakMap<api.OrganizationAllContent, Fuse<api.Space>>();
function getOrganizationSpacesFuse(orgAllContent: api.OrganizationAllContent) {
    const cachedOrganizationSpacesFuse = organizationSpacesFuseCache.get(orgAllContent);
    if (cachedOrganizationSpacesFuse) {
        return cachedOrganizationSpacesFuse;
    }

    const allSpaces = flattenSpacesInOrgAllContent(orgAllContent);
    const fuse = new Fuse(allSpaces, {
        // Reduce default 0.6 threshold for better matching
        threshold: 0.35,
        keys: ['title'],
    });

    organizationSpacesFuseCache.set(orgAllContent, fuse);
    return fuse;
}

/**
 * Fetch and compute a list of content search results (pages, spaces, sections).
 * Results are grouped smartly by space and page; and provided as a flat list.
 */
export function useContentSearchResults({
    organizationId,
    query,
}: {
    organizationId: string | null;
    query: string;
}): ContentSearchResult[] {
    const results = organizationId ? searchCache.read({ organizationId, query }) : [];
    const hiveClient = useHiveClient();

    const spaceSearchResults = React.useMemo(
        () => (!organizationId ? [] : searchSpaces(query, organizationId)),
        [query, organizationId]
    );

    return React.useMemo(() => {
        return mapSearchResults([...results, ...spaceSearchResults], {
            organization: organizationId,
            hiveClient,
        });
    }, [results, spaceSearchResults, organizationId, hiveClient]);
}

/**
 * Build a tree of groups of results.
 */
function mapSearchResults(searchResults: InAppSearchEntryObject[], context: SearchResultsContext) {
    const items = mapSuspenseArray(searchResults, (entry: InAppSearchEntryObject) =>
        mapSearchEntryObject(entry, context)
    );
    const results: ContentSearchResult[] = [];

    const keys = new Set<string>();
    const groupIndexes = new Map<string, number>();

    const locateLastFromGroup = (groups: ContentSearchGroup[]): number | null => {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup) {
            return null;
        }

        const index = groupIndexes.get(getGroupId(lastGroup));
        if (index === undefined) {
            return locateLastFromGroup(groups.slice(0, -1));
        }

        return index;
    };

    /** Insert an item after the last from a common group or replace it if needed */
    const insertItem = (item: ContentSearchResult) => {
        if (keys.has(item.key)) {
            // Replace the item if it already exists
            const index = results.findIndex((result) => result.key === item.key);
            results.splice(index, 1, item);
            return;
        }

        keys.add(item.key);

        const insertAt = locateLastFromGroup(item.groups) || results.length;
        results.splice(insertAt, 0, item);

        // Keep track of group indexes.
        const existingGroups = item.groups.map((group) => {
            const groupId = getGroupId(group);
            groupIndexes.set(groupId, insertAt + 1);
            return groupId;
        });

        // Re-index the _other_ group indexes after the insertion
        groupIndexes.forEach((index, groupId) => {
            if (index > insertAt && !existingGroups.includes(groupId)) {
                groupIndexes.set(groupId, index + 1);
            }
        });
    };

    items.forEach((item) => {
        if (!item) {
            return;
        }

        switch (item.type) {
            case 'page':
            case 'space': {
                insertItem(item);
                break;
            }
            case 'section': {
                const pageInsertAt = locateLastFromGroup([item.groups[item.groups.length - 1]]);
                if (pageInsertAt === null) {
                    // When inserting sections, we want to make sure they follow a page item
                    // even if it was not a search result
                    insertItem(mapPageToSearchResult(item.space, item.revision, item.page));
                }

                insertItem(item);
                break;
            }
            default:
                assertNever(item);
        }
    });

    return results;
}

function mapSearchEntryObject(
    entry: InAppSearchEntryObject,
    context: SearchResultsContext
): ContentSearchResult | null {
    if ('kind' in entry) {
        return {
            type: 'space',
            key: `space:${entry.space.id}`,
            title: entry.space.title,
            space: entry.space,
            groups: [],
        };
    }

    switch (entry.index) {
        case 'pages': {
            const resolved = resolvePage(
                {
                    spaceId: entry.object.space,
                    pageId: entry.object.page,
                    organizationId: entry.object.organization || null,
                },
                context
            );
            if (!resolved) {
                return null;
            }

            return {
                type: 'page',
                key: `page:${resolved.space.id}:${resolved.page.id}`,
                title: entry.object.title,
                path: hiveCore.getAbsolutePagePathInRevision(resolved.revision, resolved.page.id),
                space: resolved.space,
                revision: resolved.revision,
                page: resolved.page,
                groups: getGroupsForPage(resolved.space, resolved.revision, resolved.page),
            };
        }
        case 'sections': {
            const resolved = resolvePage(
                {
                    spaceId: entry.object.space,
                    pageId: entry.object.page,
                    organizationId: entry.object.organization || null,
                },
                context
            );
            if (!resolved) {
                return null;
            }

            return {
                type: 'section',
                key: `section:${entry.object.objectID}`,
                title: entry.object.title,
                body: entry.object.body,
                space: resolved.space,
                revision: resolved.revision,
                page: resolved.page,
                path: hiveCore.getAbsolutePagePathInRevision(resolved.revision, resolved.page.id),
                anchor: entry.object.anchor,
                groups: [
                    ...getGroupsForPage(resolved.space, resolved.revision, resolved.page),
                    {
                        type: 'page',
                        space: resolved.space,
                        page: resolved.page,
                    },
                ],
            };
        }
        default:
            assertNever(entry);
    }
}

function mapPageToSearchResult(
    space: api.Space,
    revision: hiveCore.Revision,
    page: hiveCore.RevisionPage
): ContentSearchResultPage {
    return {
        type: 'page',
        key: `page:${space.id}:${page.id}`,
        title: page.title,
        path: hiveCore.getAbsolutePagePathInRevision(revision, page.id),
        space,
        revision,
        page,
        groups: getGroupsForPage(space, revision, page),
    };
}

function resolvePage(
    entry: { spaceId: string; pageId: string; organizationId: string | null },
    { organization, hiveClient }: SearchResultsContext
): { space: api.Space; revision: hiveCore.Revision; page: hiveCore.RevisionPage } | null {
    const { spaceId, pageId, organizationId = organization } = entry;
    if (!organizationId) {
        return null;
    }

    const organizationAllContent = readAPICache(
        apiResource('getOrganizationAllContent', { organizationId }, { nullOnError: true })
    );
    if (!organizationAllContent) {
        return null;
    }

    const spaceItem = getSpaceFromOrgAllContent(organizationAllContent, spaceId);
    if (!spaceItem) {
        return null;
    }

    const { space } = spaceItem.item;
    if (!space) {
        return null;
    }

    const sourceRevision = getSuspenseSafe(() =>
        hiveClient.store.getRevision(spaceId, space.revision, { metadata: false })
    );
    if (!sourceRevision) {
        return null;
    }

    const computedRevision = getSuspenseSafe(() =>
        hiveClient.getContentComputer(sourceRevision.contentId).getCompleteRevision(sourceRevision)
    );

    const revision = computedRevision?.revision;
    const page = revision?.pages[pageId];
    if (!revision || !page) {
        return null;
    }

    return { space, revision, page };
}

function getGroupsForPage(
    space: api.Space,
    revision: hiveCore.Revision,
    page: hiveCore.RevisionPage
): ContentSearchGroup[] {
    const ancestors = hiveCore.getPageAncestorsInRevision(revision, page.id);
    const ancestorGroup =
        ancestors[0]?.type === hiveCore.RevisionPageType.Group ? ancestors[0] : null;

    return [
        {
            type: 'space',
            space,
        },
        ...(ancestorGroup
            ? ([
                  {
                      type: 'page',
                      space,
                      page: ancestorGroup,
                  },
              ] as ContentSearchGroupPage[])
            : []),
    ];
}

function getGroupId(group: ContentSearchGroup): string {
    switch (group.type) {
        case 'user':
            return `user:${group.user.uid}`;
        case 'organization':
            return `organization:${group.organization.id}`;
        case 'space':
            return `space:${group.space.id}`;
        case 'page':
            return `page:${group.space.id}:${group.page.id}`;
    }
}
